/**
 * Customer Audit Profile V2 — CLEAN questions-only view.
 *
 * Contract per user spec (Feb 2026):
 *   • Every non-credential question renders BOTH a text field AND an
 *     upload button. User may fill one or both.
 *   • Test Credentials system row keeps the structured (AES-encrypted)
 *     table — unchanged.
 *   • Archive / Unarchive is a DIRECT button beside every question —
 *     no dropdown, no kebab.
 *   • Page chrome is limited to four actions: Save, Export PDF (owner
 *     only), Add your own, Show archived toggle.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Archive, ArchiveRestore, Plus, Save, Upload, Eye, EyeOff, Trash2, Download, Lock,
  GripVertical, ArrowUpDown, X,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FileViewer from '../FileViewer';

const MASK = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';
const UPLOAD_URL = '/api/audit-profile/intake/upload';

// Best-effort MIME lookup from a filename, so FileViewer can pick the
// right renderer when the browser doesn't set blob.type (e.g. blobs
// fetched from an authenticated endpoint).
const MIME_BY_EXT = {
  pdf: 'application/pdf',
  txt: 'text/plain', csv: 'text/csv', json: 'application/json',
  md: 'text/markdown', html: 'text/html',
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml',
  mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm',
  mp3: 'audio/mpeg', wav: 'audio/wav', ogg: 'audio/ogg',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};
const guessMime = (name) => {
  const ext = (name || '').toLowerCase().split('.').pop();
  return MIME_BY_EXT[ext] || '';
};
// Office extensions that render via Microsoft's public Office Online viewer.
const OFFICE_EXTS = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']);
const extOf = (name) => (name || '').toLowerCase().split('.').pop();
const isOfficeFile = (name) => OFFICE_EXTS.has(extOf(name));
const fileIdFromUrl = (url) => {
  // Our upload endpoint returns "/api/audit-profile/intake/file/{id}"
  const m = /\/api\/audit-profile\/intake\/file\/([^/?#]+)/.exec(url || '');
  return m ? m[1] : '';
};

export default function AuditProfileV2Panel({ workspaceRole = '' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState('');
  const [archiveTarget, setArchiveTarget] = useState(null); // question pending archive confirm
  const [reorderMode, setReorderMode] = useState(false);
  const [itemsBackup, setItemsBackup] = useState(null); // snapshot for Cancel
  const isOwner = ['owner', 'workspace_owner'].includes((workspaceRole || '').toLowerCase());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/audit-profile-v2', { params: { include_archived: showArchived } });
      setItems(data.items || []);
      setDirty({});
    } catch {
      toast.error('Failed to load audit profile');
    } finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const markDirty = (qid, patch) =>
    setDirty((p) => ({ ...p, [qid]: { ...(p[qid] || {}), ...patch } }));

  const save = async () => {
    const patches = Object.entries(dirty);
    if (patches.length === 0) { toast.info('Nothing to save'); return; }
    setSaving(true);
    try {
      await Promise.all(patches.map(([qid, patch]) => axios.put(`/api/audit-profile-v2/answers/${qid}`, patch)));
      toast.success('Saved');
      setDirty({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setArchived = async (qid, archived) => {
    try {
      await axios.post(`/api/audit-profile-v2/answers/${qid}/${archived ? 'archive' : 'unarchive'}`);
      toast.success(archived ? 'Archived' : 'Unarchived');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const addCustom = async () => {
    if (!addText.trim()) { toast.error('Question is required'); return; }
    try {
      // Custom questions inherit the dual (text + upload) rendering; the
      // stored answer_format is left as 'text' — the UI ignores it.
      await axios.post('/api/audit-profile-v2/custom-questions', { text: addText.trim(), answer_format: 'text' });
      setAddText(''); setAddOpen(false);
      toast.success('Question added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  const exportPdf = async () => {
    if (!isOwner) { toast.error('Only workspace owners can export credentials'); return; }
    try {
      const { data } = await axios.get('/api/audit-profile-v2/export');
      const w = window.open('', '_blank');
      if (!w) { toast.error('Popup blocked'); return; }
      w.document.write(renderExportHtml(data));
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  const hasDirty = Object.keys(dirty).length > 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const startReorder = () => {
    setItemsBackup(items);
    setReorderMode(true);
    if (showArchived) setShowArchived(false); // keep the sort list clean
  };
  const cancelReorder = () => {
    if (itemsBackup) setItems(itemsBackup);
    setItemsBackup(null);
    setReorderMode(false);
  };
  const saveReorder = async () => {
    const ordered_ids = items.map((it) => it.question_id);
    try {
      await axios.post('/api/audit-profile-v2/reorder', { ordered_ids });
      toast.success('Order saved');
      setItemsBackup(null);
      setReorderMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reorder save failed');
    }
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const ids = prev.map((it) => it.question_id);
      const oldIdx = ids.indexOf(active.id);
      const newIdx = ids.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  return (
    <div data-testid="audit-v2-panel">
      {/* Top actions. In reorder mode the row swaps to Save-order / Cancel. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 justify-end">
        {reorderMode ? (
          <>
            <span className="text-xs text-[#0F62FE] mr-auto inline-flex items-center gap-1.5">
              <ArrowUpDown className="w-3.5 h-3.5" /> Drag questions into the order you want, then Save order.
            </span>
            <Button
              variant="outline"
              onClick={cancelReorder}
              className="rounded-[10px]"
              data-testid="audit-v2-reorder-cancel"
            >
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={saveReorder}
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white rounded-[10px]"
              data-testid="audit-v2-reorder-save"
            >
              <Save className="w-4 h-4 mr-1" /> Save order
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={() => setShowArchived((v) => !v)}
              className="rounded-[10px] mr-auto"
              data-testid="audit-v2-toggle-archived"
            >
              {showArchived ? (
                <><ArchiveRestore className="w-4 h-4 mr-1" /> Hide archived</>
              ) : (
                <><Archive className="w-4 h-4 mr-1" /> Show archived</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={startReorder}
              className="rounded-[10px]"
              data-testid="audit-v2-reorder-btn"
              title="Drag questions into any order"
            >
              <ArrowUpDown className="w-4 h-4 mr-1" /> Reorder
            </Button>
            <Button variant="outline" onClick={() => setAddOpen(true)}
              className="rounded-[10px]" data-testid="audit-v2-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add your own
            </Button>
            {isOwner && (
              <Button variant="outline" onClick={exportPdf} className="rounded-[10px]"
                data-testid="audit-v2-export-pdf" title="Owner-only. PDF contains live credentials.">
                <Download className="w-4 h-4 mr-1" /> Export PDF
              </Button>
            )}
            <Button onClick={save} disabled={!hasDirty || saving}
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white rounded-[10px]"
              data-testid="audit-v2-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        )}
      </div>

      {addOpen && (
        <div className="mb-4 rounded-xl border border-[#0F62FE]/30 bg-[#F1F5FF] p-4"
          data-testid="audit-v2-add-form">
          <Textarea value={addText} onChange={(e) => setAddText(e.target.value)}
            placeholder="Add a question specific to your workspace…"
            rows={2} maxLength={600} data-testid="audit-v2-add-text" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => { setAddOpen(false); setAddText(''); }}
              className="rounded-[10px]" data-testid="audit-v2-add-cancel">Cancel</Button>
            <Button onClick={addCustom}
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white rounded-[10px]"
              data-testid="audit-v2-add-save">Add question</Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="p-6 text-center text-sm text-[#94A3B8]">Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="p-6 text-center text-sm text-[#64748B]">No questions yet.</div>
      )}

      <div className="space-y-3">
        {reorderMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={items.map((it) => it.question_id)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((it, idx) => (
                <SortableQuestionRow
                  key={it.question_id}
                  index={idx + 1}
                  it={it}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          items.map((it, idx) => (
            <QuestionRow
              key={it.question_id}
              index={idx + 1}
              it={it}
              dirtyPatch={dirty[it.question_id] || {}}
              onChange={(patch) => markDirty(it.question_id, patch)}
              onArchiveRequest={() => setArchiveTarget(it)}
              onUnarchive={() => setArchived(it.question_id, false)}
              workspaceRole={workspaceRole}
            />
          ))
        )}
      </div>

      {/* Archive confirmation — mirrors the Super Admin flow. */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="audit-v2-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this question?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2"><b>“{archiveTarget?.text}”</b></span>
              This will hide the question from your Audit Profile. Nothing is deleted —
              turn on <b>Show archived</b> above and click Unarchive to bring it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="audit-v2-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const t = archiveTarget;
                setArchiveTarget(null);
                if (t) await setArchived(t.question_id, true);
              }}
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white"
              data-testid="audit-v2-archive-confirm-btn"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* -------------------------------------------------------------------- */
function QuestionRow({ index, it, dirtyPatch, onChange, onArchiveRequest, onUnarchive, workspaceRole }) {
  const isCreds = it.answer_format === 'test_credentials';
  return (
    <div
      className={`rounded-xl border p-4 ${it.archived ? 'bg-[#FBFBFC] border-[#E2E8F0] opacity-70' : 'bg-white border-[#E2E8F0]'}`}
      data-testid={`audit-v2-row-${it.question_id}`}
    >
      {/* Header row — question text + action pair on the right. */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0 flex items-start gap-2">
          <span className="text-[13px] font-semibold text-[#94A3B8] shrink-0 tabular-nums mt-0.5">
            {String(index).padStart(2, '0')}.
          </span>
          <span className="text-[14px] font-medium text-[#0A0A0A] leading-snug">{it.text}</span>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {!isCreds && !it.archived && (
            <CompactUploadButton
              onFiles={(newRefs) => {
                const existing = dirtyPatch.pdf_refs ?? it.pdf_refs ?? [];
                const combined = [...existing, ...newRefs].slice(0, 20);
                onChange({ pdf_refs: combined });
              }}
              testid={`audit-v2-upload-${it.question_id}`}
            />
          )}
          {it.archived ? (
            <Button
              size="sm"
              variant="outline"
              onClick={onUnarchive}
              className="rounded-[8px] h-8 px-2.5 text-[#047857] border-[#A7F3D0] hover:bg-[#ECFDF5]"
              data-testid={`audit-v2-unarchive-${it.question_id}`}
            >
              <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Unarchive
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onArchiveRequest}
              className="rounded-[8px] h-8 px-2.5 text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC] disabled:opacity-40"
              data-testid={`audit-v2-archive-${it.question_id}`}
              title="Archive question"
              disabled={it.system}
            >
              <Archive className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Answer body — full width. */}
      {!it.archived && (
        isCreds ? (
          <CredentialsTable
            rows={dirtyPatch.credentials ?? it.credentials ?? []}
            onChange={(credentials) => onChange({ credentials })}
            testid={`audit-v2-creds-${it.question_id}`}
            workspaceRole={workspaceRole}
            questionId={it.question_id}
          />
        ) : (
          <>
            <Textarea
              value={dirtyPatch.text_value ?? it.text_value ?? ''}
              onChange={(e) => onChange({ text_value: e.target.value })}
              rows={2}
              placeholder="Type your answer…"
              className="w-full"
              data-testid={`audit-v2-ans-${it.question_id}-text`}
            />
            {((dirtyPatch.pdf_refs ?? it.pdf_refs) || []).map((ref, i) => (
              <UploadedFileActions
                key={`${ref.url}-${i}`}
                fileUrl={ref.url}
                initialFilename={ref.filename}
                onRemove={() => {
                  const cur = dirtyPatch.pdf_refs ?? it.pdf_refs ?? [];
                  const next = cur.filter((_, idx) => idx !== i);
                  onChange({ pdf_refs: next });
                }}
                testid={`audit-v2-ans-${it.question_id}-f${i}`}
              />
            ))}
          </>
        )
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
function SortableQuestionRow({ index, it }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: it.question_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    boxShadow: isDragging ? '0 8px 24px rgba(15, 23, 42, 0.12)' : undefined,
    background: isDragging ? '#F1F5FF' : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-xl border border-[#E2E8F0] bg-white p-3 flex items-center gap-3"
      data-testid={`audit-v2-reorder-row-${it.question_id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 text-[#94A3B8] hover:text-[#0F62FE] cursor-grab active:cursor-grabbing p-1 -m-1 rounded touch-none"
        title="Drag to reorder"
        data-testid={`audit-v2-drag-${it.question_id}`}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-[13px] font-semibold text-[#94A3B8] shrink-0 tabular-nums w-8">
        {String(index).padStart(2, '0')}.
      </span>
      <span className="text-[13px] text-[#0A0A0A] flex-1 truncate">{it.text}</span>
      {it.is_custom && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE] shrink-0">
          Custom
        </span>
      )}
      {it.system && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#5B21B6] border border-[#DDD6FE] shrink-0">
          System
        </span>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
function UploadedFileActions({ fileUrl, initialFilename, onRemove, testid }) {
  const [busy, setBusy] = useState('');
  const [resolvedName, setResolvedName] = useState(initialFilename || '');
  const [previewState, setPreviewState] = useState(null); // { blobUrl, filename }
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');

  // If we don't already know the filename, resolve it lazily from
  // Content-Disposition the first time this row renders. Uses HEAD-like
  // range fetch to keep the payload tiny (1 byte).
  useEffect(() => {
    if (resolvedName || !fileUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token') || '';
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const full = /^https?:\/\//i.test(fileUrl) ? fileUrl : `${base}${fileUrl}`;
        const r = await fetch(full, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Range: 'bytes=0-0',
          },
        });
        const cd = r.headers.get('content-disposition') || '';
        const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
        const fn = m ? decodeURIComponent(m[1]) : (fileUrl.split('/').pop() || 'file');
        if (!cancelled) setResolvedName(fn);
      } catch { /* silent — filename display is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [fileUrl, resolvedName]);

  const fetchBlob = async () => {
    const token = localStorage.getItem('token') || '';
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const full = /^https?:\/\//i.test(fileUrl) ? fileUrl : `${base}${fileUrl}`;
    const r = await fetch(full, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const cd = r.headers.get('content-disposition') || '';
    const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
    const filename = m ? decodeURIComponent(m[1]) : (resolvedName || fileUrl.split('/').pop() || 'file');
    return { blob: await r.blob(), filename };
  };

  const preview = async () => {
    setBusy('preview');
    try {
      // Office docs (doc/docx/xls/xlsx/ppt/pptx) render via Microsoft's
      // public Office Online viewer, which needs a publicly reachable
      // signed URL. Everything else uses the in-tab blob approach.
      if (isOfficeFile(resolvedName || fileUrl)) {
        const fileId = fileIdFromUrl(fileUrl);
        if (!fileId) throw new Error('Cannot preview this file — missing id');
        const { data } = await axios.get(`/api/audit-profile/intake/preview-url/${fileId}`);
        setPreviewState({
          officeEmbedUrl: data.office_embed_url,
          filename: data.filename || resolvedName || 'document',
          mime: data.content_type || guessMime(data.filename),
        });
        return;
      }
      const { blob, filename } = await fetchBlob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewState({ blobUrl, filename, mime: blob.type || guessMime(filename) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Preview failed');
    } finally { setBusy(''); }
  };

  const closePreview = () => {
    if (previewState?.blobUrl) {
      // Revoke shortly after close so any in-flight iframe fetch finishes.
      const url = previewState.blobUrl;
      setTimeout(() => URL.revokeObjectURL(url), 2_000);
    }
    setPreviewState(null);
  };

  const download = async () => {
    setBusy('download');
    try {
      const { blob, filename } = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (e) {
      toast.error('Download failed');
    } finally { setBusy(''); }
  };

  const confirmDelete = () => {
    if (deleteInput.trim().toLowerCase() !== 'delete') {
      toast.error('Type "delete" to confirm');
      return;
    }
    onRemove();
    setDeleteInput('');
    setDeleteOpen(false);
  };

  return (
    <div className="mt-2 flex items-center flex-wrap gap-2" data-testid={`${testid}-file-actions`}>
      <span
        className="inline-flex items-center gap-1 text-[11px] text-[#475569] px-2 py-1 rounded bg-[#F1F5F9] border border-[#E2E8F0] max-w-[260px]"
        data-testid={`${testid}-filename`}
        title={resolvedName || 'Uploaded file'}
      >
        <Eye className="w-3 h-3 text-[#94A3B8]" />
        <span className="truncate">{resolvedName || 'Uploaded file'}</span>
      </span>
      <button
        type="button"
        onClick={preview}
        disabled={busy === 'preview'}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-[6px] border border-[#E2E8F0] text-[11px] text-[#475569] hover:bg-[#F0F9FF] hover:text-[#0369A1] hover:border-[#BAE6FD] transition-colors disabled:opacity-50"
        data-testid={`${testid}-preview`}
        title="Preview file in a popup"
      >
        <Eye className="w-3 h-3" /> Preview
      </button>
      <button
        type="button"
        onClick={download}
        disabled={busy === 'download'}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-[6px] border border-[#E2E8F0] text-[11px] text-[#475569] hover:bg-[#ECFDF5] hover:text-[#047857] hover:border-[#A7F3D0] transition-colors disabled:opacity-50"
        data-testid={`${testid}-download`}
        title="Download this file"
      >
        <Download className="w-3 h-3" /> Download
      </button>
      <button
        type="button"
        onClick={() => setDeleteOpen(true)}
        className="inline-flex items-center justify-center h-7 w-7 rounded-[6px] text-[#94A3B8] hover:text-[#B91C1C] hover:bg-[#FEF2F2]"
        title="Delete this file"
        data-testid={`${testid}-remove`}
      >
        <Trash2 className="w-3 h-3" />
      </button>

      {/* Inline preview modal — reuses the shared FileViewer with a
          fetched blob URL so authenticated files render in-app. */}
      <FileViewer
        open={!!previewState}
        onClose={closePreview}
        directUrl={previewState?.blobUrl}
        officeEmbedUrl={previewState?.officeEmbedUrl}
        mime={previewState?.mime}
        filename={previewState?.filename}
      />

      {/* Hard-delete confirmation — must type "delete". */}
      <AlertDialog open={deleteOpen} onOpenChange={(o) => { if (!o) { setDeleteOpen(false); setDeleteInput(''); } }}>
        <AlertDialogContent data-testid={`${testid}-remove-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this uploaded file?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2"><b>{resolvedName || 'Uploaded file'}</b></span>
              This unlinks the file from the question. Type <b>delete</b> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteInput}
            onChange={(e) => setDeleteInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && confirmDelete()}
            placeholder='Type "delete" to confirm'
            data-testid={`${testid}-remove-input`}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-remove-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteInput.trim().toLowerCase() !== 'delete'}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-40"
              data-testid={`${testid}-remove-confirm-btn`}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* -------------------------------------------------------------------- */
function PasswordCell({ row, onChange, testid, workspaceRole, questionId }) {
  const [visible, setVisible] = useState(false);
  const [revealed, setRevealed] = useState('');
  const [revealBusy, setRevealBusy] = useState(false);
  const inputRef = React.useRef(null);
  const revealedForIdRef = React.useRef('');  // credential id we've already fetched
  const isOwner = ['owner', 'workspace_owner'].includes((workspaceRole || '').toLowerCase());

  const raw = row.password;
  const isMaskSentinel = raw === MASK;
  const hasStored = !!row.has_password || isMaskSentinel;
  const isTouched = typeof raw === 'string' && !isMaskSentinel;

  // Fetch the real plaintext ONCE per credential so the field shows one
  // dot per actual character (5 chars stored → 5 mask dots). Owner-only —
  // for other roles the field stays blank and only accepts a new password.
  React.useEffect(() => {
    if (!row.id) return;
    if (!hasStored || isTouched) return;
    if (!isOwner) return;
    if (revealedForIdRef.current === row.id) return;
    revealedForIdRef.current = row.id;
    setRevealBusy(true);
    axios
      .post('/api/audit-profile-v2/reveal-password', {
        question_id: questionId,
        credential_id: row.id,
      })
      .then((r) => setRevealed(r.data?.password || ''))
      .catch(() => { revealedForIdRef.current = ''; /* allow retry on next mount */ })
      .finally(() => setRevealBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, hasStored, isOwner]);

  // Value shown in the input:
  //   • user typed → their text (mask dot count === length of what they typed)
  //   • saved + owner-fetched → the plaintext (masked visually by type="password")
  //   • saved + non-owner (or reveal failed) → empty; placeholder shows "Set password"
  const showValue = isTouched ? raw : revealed;
  const placeholder = hasStored && !revealed ? '' : 'Set password';

  // Place caret at end on focus so the user can immediately keep typing.
  const putCaretAtEnd = () => {
    const el = inputRef.current;
    if (!el) return;
    // Use RAF so the browser has committed focus / value before we move
    // the caret (avoids Chrome selecting the whole value on tab-focus).
    requestAnimationFrame(() => {
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch { /* number-mode inputs */ }
    });
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type={visible ? 'text' : 'password'}
        value={showValue}
        onChange={(e) => {
          // Once the user edits, drop the auto-reveal cache so what they
          // see IS what will be saved.
          if (revealed) setRevealed('');
          onChange(e.target.value);
        }}
        onFocus={putCaretAtEnd}
        onClick={putCaretAtEnd}
        placeholder={placeholder}
        className="h-8 font-mono pr-8"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={revealBusy}
        className="absolute inset-y-0 right-1 my-auto h-6 w-6 inline-flex items-center justify-center rounded text-[#94A3B8] hover:text-[#0F62FE] hover:bg-[#F1F5FF] disabled:opacity-50"
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        data-testid={`${testid}-toggle`}
      >
        {visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------- */
function CompactUploadButton({ onFiles, testid }) {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const oversize = files.find((f) => f.size > 100 * 1024 * 1024);
    if (oversize) { toast.error(`"${oversize.name}" exceeds 100MB`); e.target.value = ''; return; }
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(UPLOAD_URL, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const url = data?.url || data?.file_url || data?.path || '';
        const filename = data?.filename || f.name || '';
        if (!url) throw new Error('Upload returned no url');
        uploaded.push({ url, filename });
      }
      onFiles(uploaded);
      toast.success(uploaded.length === 1 ? 'Uploaded' : `${uploaded.length} files uploaded`);
    } catch (err) {
      // If some files uploaded before the failure, still bubble them up
      // so the user doesn't lose that work.
      if (uploaded.length) onFiles(uploaded);
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <label
      className="inline-flex items-center justify-center h-8 px-2.5 rounded-[8px] border border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC] hover:text-[#0F62FE] hover:border-[#BFDBFE] cursor-pointer transition-colors"
      title={uploading ? 'Uploading…' : 'Upload files (you can pick multiple)'}
    >
      <Upload className="w-3.5 h-3.5" />
      <input type="file" multiple className="hidden" onChange={onPick} data-testid={testid} />
    </label>
  );
}

/* -------------------------------------------------------------------- */
function CredentialsTable({ rows, onChange, testid, workspaceRole, questionId }) {
  const [deleteTarget, setDeleteTarget] = useState(null); // {index, row}
  const [deleteInput, setDeleteInput] = useState('');
  const setRow = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...(rows || []), {
    id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    url: '', role: '', username: '', password: '',
  }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const requestDelete = (i) => { setDeleteTarget({ index: i, row: rows[i] }); setDeleteInput(''); };
  const confirmDelete = () => {
    if (deleteInput.trim().toLowerCase() !== 'delete') { toast.error('Type "delete" to confirm'); return; }
    if (deleteTarget) removeRow(deleteTarget.index);
    setDeleteTarget(null); setDeleteInput('');
  };

  return (
    <div data-testid={testid}>
      <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] p-2.5 mb-2 text-xs text-[#92400E] inline-flex items-center gap-2">
        <Lock className="w-3.5 h-3.5" /> Passwords are AES-256 encrypted at rest. Shown as ••••••••; owners can Export PDF to share real credentials.
      </div>
      <div className="overflow-x-auto -mx-1">
        <table className="w-full text-xs" data-testid={`${testid}-table`}>
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#94A3B8]">
              <th className="py-1 px-2">URL</th>
              <th className="py-1 px-2">Role</th>
              <th className="py-1 px-2">Username</th>
              <th className="py-1 px-2">Password</th>
              <th className="py-1 px-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1F5F9]">
            {(rows || []).map((r, i) => (
              <tr key={r.id || i} data-testid={`${testid}-row-${i}`}>
                <td className="py-1 px-2"><Input value={r.url || ''} onChange={(e) => setRow(i, { url: e.target.value })} placeholder="https://…" className="h-8" data-testid={`${testid}-url-${i}`} /></td>
                <td className="py-1 px-2"><Input value={r.role || ''} onChange={(e) => setRow(i, { role: e.target.value })} placeholder="Auditor" className="h-8" data-testid={`${testid}-role-${i}`} /></td>
                <td className="py-1 px-2"><Input value={r.username || ''} onChange={(e) => setRow(i, { username: e.target.value })} placeholder="auditor@…" className="h-8" data-testid={`${testid}-user-${i}`} /></td>
                <td className="py-1 px-2">
                  <PasswordCell
                    row={r}
                    onChange={(password) => setRow(i, { password })}
                    testid={`${testid}-pwd-${i}`}
                    workspaceRole={workspaceRole}
                    questionId={questionId}
                  />
                </td>
                <td className="py-1 px-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => requestDelete(i)}
                    className="text-[#94A3B8] hover:text-[#B91C1C]"
                    title="Delete this credential row"
                    data-testid={`${testid}-del-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="outline" onClick={addRow}
        className="rounded-lg mt-2" data-testid={`${testid}-add`}>
        <Plus className="w-3.5 h-3.5 mr-1" /> Add row
      </Button>

      {/* Hard-delete confirmation — must type "delete". */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteInput(''); } }}>
        <AlertDialogContent data-testid={`${testid}-del-confirm`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this credential row?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2 font-mono text-[12px] text-[#0A0A0A]">
                {deleteTarget?.row?.url || '(no URL)'} — {deleteTarget?.row?.username || '(no username)'}
              </span>
              This removes the URL, role, username, and the encrypted password from
              this workspace. Type <b>delete</b> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteInput}
            onChange={(e) => setDeleteInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && confirmDelete()}
            placeholder='Type "delete" to confirm'
            autoFocus
            data-testid={`${testid}-del-input`}
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-del-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteInput.trim().toLowerCase() !== 'delete'}
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-40"
              data-testid={`${testid}-del-confirm-btn`}
            >
              Delete row
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* -------------------------------------------------------------------- */
function renderExportHtml(data) {
  const esc = (s) => String(s || '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const rows = (data?.items || []).map((it) => {
    if (it.answer_format === 'test_credentials') {
      const creds = (it.credentials_decrypted || []).map((c) => `
        <tr><td>${esc(c.url)}</td><td>${esc(c.role)}</td><td>${esc(c.username)}</td><td><code>${esc(c.password)}</code></td></tr>`).join('');
      return `
        <div class="q">
          <h3>${esc(it.text)}</h3>
          <table><thead><tr><th>URL</th><th>Role</th><th>Username</th><th>Password</th></tr></thead>
          <tbody>${creds || '<tr><td colspan="4"><i>No rows</i></td></tr>'}</tbody></table>
        </div>`;
    }
    // Non-credential questions: text answer AND/OR uploaded file.
    const textBlock = it.text_value
      ? `<p>${esc(it.text_value).replace(/\n/g, '<br/>')}</p>`
      : '';
    const fileBlock = it.pdf_ref
      ? `<p>File: <a href="${esc(it.pdf_ref)}">${esc(it.pdf_ref)}</a></p>`
      : '';
    const body = textBlock + fileBlock || '<p><i>(no answer)</i></p>';
    return `<div class="q"><h3>${esc(it.text)}</h3>${body}</div>`;
  }).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Audit Profile Export</title>
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;color:#0A0A0A;max-width:800px;margin:24px auto;padding:0 24px;}
  .banner{background:#FEE2E2;color:#B91C1C;border:1px solid #FCA5A5;border-radius:6px;padding:10px;margin-bottom:16px;font-size:12px;font-weight:600;}
  .q{margin-bottom:20px;border-bottom:1px solid #E2E8F0;padding-bottom:12px;}
  h1{font-size:20px;margin:0 0 8px 0;} h3{font-size:14px;margin:0 0 6px 0;}
  table{width:100%;border-collapse:collapse;font-size:12px;} th,td{border:1px solid #E2E8F0;padding:6px;text-align:left;}
  th{background:#F8FAFC;font-size:11px;text-transform:uppercase;color:#64748B;letter-spacing:0.05em;}
  code{font-family:Menlo,monospace;background:#F1F5F9;padding:1px 4px;border-radius:3px;}
</style></head><body>
<h1>Audit Profile</h1>
<div class="banner">⚠ This document contains live credentials. Handle with care and store in your secrets manager.</div>
${rows}
</body></html>`;
}
